/**
 * Offline-first fleet store — CRUD over the `vehicles` and `soldiers` tables
 * introduced in database version 3.
 *
 * Reads are open; every mutation goes through the permissions engine, so a
 * work manager whose `MANAGE_SOLDIERS` flag was revoked cannot edit the roster
 * even though they can still see it. Matching questions are answered by
 * `checkDriverVehicleMatch()` alone — this module never re-implements them.
 *
 * A removal is a clean one: the row goes, and every command-level block list
 * that named it is swept in the same breath, so no block survives the record it
 * was written about. A record still riding on a convoy is never removed at all
 * — the assignment store is asked first, and the delete is refused.
 *
 * Nothing here seeds demo records. Both tables open on the company's own data:
 * the roster from `src/core/seed-soldiers.ts` and the twenty-eight vehicles
 * from `src/core/seed-vehicles.ts` — see `seedSoldierRoster()` and
 * `seedVehicleRoster()`, each of which writes into an empty table alone.
 *
 * Where a vehicle *is* is a first-class operation here: `returnVehicleToBase()`
 * and `assignVehicleToCompany()` are the two transfers the roster offers, and
 * both build their patch in `src/core/vehicle-location.ts` so the location and
 * the operational status can never drift apart. Each move that actually moves
 * something is filed in the audit ledger too — that ledger is the loan history
 * "כרטיס רכב" reads back.
 */
import { db } from './index';
import { countSoldierAssignments, countVehicleAssignments } from './assignment-store';
import { recordFleetChange, recordVehicleTransfer } from './audit-store';
import {
  diffSoldier,
  diffVehicle,
  soldierChangeLabel,
  vehicleChangeLabel,
} from '../core/fleet-changes';
import { readMeta, writeMeta } from './meta-store';
import {
  ROSTER_NAME_CORRECTIONS,
  ROSTER_REVISION,
  SEED_SOLDIERS,
  isLegacyDemoSoldier,
} from '../core/seed-soldiers';
import {
  SEED_VEHICLES,
  VEHICLE_ROSTER_REVISION,
  plateKey,
} from '../core/seed-vehicles';
import {
  HOME_COMPANY,
  isHomeCompany,
  loanToCompanyPatch,
  normalizeCompany,
  returnToBasePatch,
} from '../core/vehicle-location';
import { rememberCompany } from '../core/company-directory';
import { formatMilitaryId, vehicleCallSign } from '../core/military-id';
import {
  SOLDIER_ROLE_REVISION,
  isLegacySoldierRole,
  normalizeSoldierRole,
} from '../core/soldier-roles';
import { assertPermission } from '../core/permissions';
import { stampCreated, stampUpdated } from '../core/audit-stamp';
import { emitStoreChange } from '../core/store-events';
import { checkDriverVehicleMatch, findEligibleDrivers } from '../core/driver-matcher';
import type { MatchOptions } from '../core/driver-matcher';
import type { DriverVehicleMatch, Soldier, Vehicle } from '../types/fleet';
import type { User } from '../types/user';

const nowIso = (now: number = Date.now()): string => new Date(now).toISOString();

const newId = (prefix: string): string => `${prefix}-${crypto.randomUUID()}`;

/**
 * The caller-supplied part of a record — ids and stamps are ours, and so are
 * the two audit names: a form may not nominate who made the change, or the
 * indicator on the roster would be a claim rather than a record.
 */
export type VehicleDraft = Omit<
  Vehicle,
  'id' | 'createdAt' | 'updatedAt' | 'createdByName' | 'updatedByName'
>;
export type SoldierDraft = Omit<
  Soldier,
  'id' | 'createdAt' | 'updatedAt' | 'createdByName' | 'updatedByName'
>;

/**
 * Raised when a delete is refused because the record is still bound to a
 * convoy. The message is the sentence the screen shows; `assignments` is how
 * many teams or crew rows hold it.
 */
export class FleetRecordInUseError extends Error {
  readonly assignments: number;

  constructor(message: string, assignments: number) {
    super(message);
    this.name = 'FleetRecordInUseError';
    this.assignments = assignments;
  }
}

// ── Vehicles ─────────────────────────────────────────────────────────

/**
 * The pool, by the number each vehicle is called by — the internal number where
 * one is recorded, the tail of the מספר צ where it is not. Plate breaks the tie,
 * so two vehicles that answer to the same tail still read in a stable order.
 */
export async function listVehicles(): Promise<Vehicle[]> {
  const vehicles = await db.vehicles.toArray();
  return vehicles.sort((a, b) => {
    const byNumber = vehicleCallSign(a).localeCompare(vehicleCallSign(b));
    return byNumber !== 0 ? byNumber : a.plateNumber.localeCompare(b.plateNumber);
  });
}

export async function getVehicle(vehicleId: string): Promise<Vehicle | null> {
  return (await db.vehicles.get(vehicleId)) ?? null;
}

/** Lookup by the three-digit internal number, the way people actually search. */
export async function getVehicleByMilitaryId(militaryId: string): Promise<Vehicle | null> {
  const normalized = formatMilitaryId(militaryId);
  if (!normalized) return null;
  return (await db.vehicles.where('militaryId').equals(normalized).first()) ?? null;
}

/**
 * The internal number as it is stored: three digits, or an empty string when
 * the unit has not recorded one. Anything else is refused.
 *
 * A blank is a legitimate answer — the twenty-eight vehicles the company handed
 * over came with plates and no internal numbers — so it is `null`/`''` that
 * passes here and `'12a'` that throws.
 */
function normalizeVehicleMilitaryId(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || String(raw).trim() === '') return '';
  const militaryId = formatMilitaryId(raw);
  if (!militaryId) throw new Error('Vehicle militaryId must be 1–3 digits');
  return militaryId;
}

/**
 * Refuses an internal number another vehicle already carries.
 *
 * `db.version(11)` gave up the unique index — it cannot hold twenty-eight
 * unrecorded numbers — so the constraint lives here instead, over the numbers
 * that are actually recorded. The message says "constraint" because that is
 * what the vehicle form matches on to explain the refusal in Hebrew.
 */
async function assertMilitaryIdFree(militaryId: string, exceptId: string | null): Promise<void> {
  if (!militaryId) return;
  const holder = await db.vehicles.where('militaryId').equals(militaryId).first();
  if (holder && holder.id !== exceptId) {
    throw new Error(`ConstraintError: vehicle militaryId ${militaryId} is already in use`);
  }
}

/**
 * Lookup by מספר צ, matched on the digits alone — `899-503`, `צ 899-503` and
 * `899503` are the same vehicle. The plate is what identifies a vehicle the
 * unit has not painted an internal number onto yet.
 */
export async function getVehicleByPlate(plateNumber: string): Promise<Vehicle | null> {
  const key = plateKey(plateNumber);
  if (!key) return null;
  const vehicles = await db.vehicles.toArray();
  return vehicles.find((vehicle) => plateKey(vehicle.plateNumber) === key) ?? null;
}

export async function createVehicle(actor: User, draft: VehicleDraft): Promise<Vehicle> {
  assertPermission(actor, 'MANAGE_VEHICLES');
  const militaryId = normalizeVehicleMilitaryId(draft.militaryId);
  await assertMilitaryIdFree(militaryId, null);
  const stamp = nowIso();
  const vehicle: Vehicle = {
    ...draft,
    militaryId,
    // A vehicle added by hand is standing in front of whoever added it.
    assignedCompany: normalizeCompany(draft.assignedCompany) || HOME_COMPANY,
    id: newId('veh'),
    createdAt: stamp,
    updatedAt: stamp,
    // "מי עידכן מה" — signed at birth, so a vehicle added mid-shift already
    // names its author on the roster row without anybody editing it.
    ...stampCreated(actor),
  };
  await db.vehicles.add(vehicle);
  emitStoreChange('fleet');
  return vehicle;
}

/** How a write asks to be filed in the ledger. */
interface FleetWriteOptions {
  /**
   * The caller files its own, richer row — the generic change row is skipped so
   * one action does not read as two. Only the transfers use it: a loan is
   * already a line of history, and "מחזיקה: שינוע ← אוגדה" beside it would say
   * the same thing twice.
   */
  skipChangeLog?: boolean;
}

/**
 * The single write path for a vehicle. Everything that changes a vehicle comes
 * through here, which is what guarantees no edit can reach the table without
 * the ledger hearing about it.
 */
async function writeVehicle(
  actor: User,
  vehicleId: string,
  patch: Partial<VehicleDraft>,
  { skipChangeLog = false }: FleetWriteOptions = {},
): Promise<Vehicle | null> {
  assertPermission(actor, 'MANAGE_VEHICLES');
  const vehicle = await getVehicle(vehicleId);
  if (!vehicle) return null;
  const militaryId =
    patch.militaryId === undefined
      ? vehicle.militaryId
      : normalizeVehicleMilitaryId(patch.militaryId);
  await assertMilitaryIdFree(militaryId, vehicle.id);
  // Every status tap, every transfer and every form save lands here, which is
  // what makes this one line enough to put "עודכן ע״י …" on every vehicle row.
  const updated: Vehicle = {
    ...vehicle,
    ...patch,
    militaryId,
    updatedAt: nowIso(),
    ...stampUpdated(actor),
  };
  await db.vehicles.put(updated);
  if (!skipChangeLog) {
    // An empty diff writes nothing — a re-saved form is not a change.
    await recordFleetChange(
      actor,
      'vehicle',
      updated.id,
      vehicleChangeLabel(updated),
      diffVehicle(vehicle, updated),
    );
  }
  // Announced rather than merely mirrored: every open screen re-reads on this
  // call, and the cloud push rides along with it. A status tapped here should be
  // on the מ"פ's screen in a moment, not in eight seconds.
  emitStoreChange('fleet');
  return updated;
}

export async function updateVehicle(
  actor: User,
  vehicleId: string,
  patch: Partial<VehicleDraft>,
): Promise<Vehicle | null> {
  return writeVehicle(actor, vehicleId, patch);
}

// ── Vehicle location ─────────────────────────────────────────────────

/**
 * Files the move in the audit ledger, which is where a vehicle's loan history
 * is read back from. A move that changes nothing is not a line of history: a
 * "החזר לשינוע" pressed on a vehicle that never left, or a loan to the company
 * already holding it, is filed nowhere.
 */
async function fileVehicleTransfer(actor: User, before: Vehicle, to: string): Promise<void> {
  const from = normalizeCompany(before.assignedCompany);
  const target = normalizeCompany(to);
  if (target === from) return;
  if (isHomeCompany(target) && isHomeCompany(from)) return;
  const label = `${vehicleCallSign(before)} · ${before.name || before.model}`;
  await recordVehicleTransfer(actor, before.id, from, target, label);
}

/**
 * "החזר לשינוע" — the vehicle is back with the company and available again.
 * `null` means there was no such vehicle.
 */
export async function returnVehicleToBase(
  actor: User,
  vehicleId: string,
): Promise<Vehicle | null> {
  const vehicle = await getVehicle(vehicleId);
  if (!vehicle) return null;
  const patch = returnToBasePatch(vehicle);
  const updated = await writeVehicle(actor, vehicleId, patch, { skipChangeLog: true });
  if (updated) await fileVehicleTransfer(actor, vehicle, patch.assignedCompany);
  return updated;
}

/**
 * "השאלה לפלוגה" — the vehicle is handed to another company. Handing it back to
 * שינוע (or passing a blank) is a return, not a loan to ourselves.
 *
 * A company the unit typed in on the spot is remembered as the loan is filed,
 * so the next sheet offers it as a button — see {@link listCustomCompanies}.
 */
export async function assignVehicleToCompany(
  actor: User,
  vehicleId: string,
  company: string,
): Promise<Vehicle | null> {
  const vehicle = await getVehicle(vehicleId);
  if (!vehicle) return null;
  const patch = loanToCompanyPatch(vehicle, company);
  const updated = await writeVehicle(actor, vehicleId, patch, { skipChangeLog: true });
  if (!updated) return updated;
  await fileVehicleTransfer(actor, vehicle, patch.assignedCompany);
  await rememberCustomCompany(patch.assignedCompany);
  return updated;
}

// ── The companies the unit lends to ──────────────────────────────────

/** Where the companies the unit added by hand are filed in the `meta` table. */
export const CUSTOM_COMPANIES_KEY = 'custom-companies';

/**
 * The companies the unit has added itself, in Hebrew order. The five standing
 * options and שינוע are never filed here — `src/core/company-directory.ts`
 * already puts those on every sheet.
 */
export async function listCustomCompanies(): Promise<string[]> {
  const stored = await readMeta<string[]>(CUSTOM_COMPANIES_KEY);
  if (!Array.isArray(stored)) return [];
  return stored.reduce<string[]>((held, name) => rememberCompany(held, name), []);
}

/**
 * Files a company the unit typed in. A blank, ourselves, a name already on the
 * list or one of the five leaves the list untouched, so the caller may write
 * unconditionally. Returns the list as it now stands.
 */
export async function rememberCustomCompany(name: string | null | undefined): Promise<string[]> {
  const held = await listCustomCompanies();
  const next = rememberCompany(held, name);
  if (next.join('|') !== held.join('|')) await writeMeta(CUSTOM_COMPANIES_KEY, next);
  return next;
}

/**
 * How many convoys a vehicle is bound to. A screen asks this before it offers
 * the delete, so the refusal is explained before it is met.
 */
export async function countVehicleUsage(vehicleId: string): Promise<number> {
  return countVehicleAssignments(vehicleId);
}

/** Drops a departed vehicle from every soldier's personal block list. */
async function forgetVehicleReferences(vehicleId: string): Promise<void> {
  const soldiers = await db.soldiers.toArray();
  const touched = soldiers.filter((soldier) =>
    (soldier.disallowedVehicleIds ?? []).includes(vehicleId),
  );
  if (touched.length === 0) return;
  const stamp = nowIso();
  await db.soldiers.bulkPut(
    touched.map((soldier) => ({
      ...soldier,
      disallowedVehicleIds: (soldier.disallowedVehicleIds ?? []).filter((id) => id !== vehicleId),
      updatedAt: stamp,
    })),
  );
}

/**
 * Removes a vehicle for good, block lists and all. A vehicle that still rides
 * on a convoy is refused with {@link FleetRecordInUseError} rather than left
 * as a dangling id on a team; `false` means there was no such vehicle.
 */
export async function deleteVehicle(actor: User, vehicleId: string): Promise<boolean> {
  assertPermission(actor, 'MANAGE_VEHICLES');
  if (!(await getVehicle(vehicleId))) return false;
  const assignments = await countVehicleAssignments(vehicleId);
  if (assignments > 0) {
    throw new FleetRecordInUseError(
      `לא ניתן למחוק רכב המשובץ ל־${assignments} משימות. יש להסיר אותו מהשיבוצים תחילה.`,
      assignments,
    );
  }
  await db.vehicles.delete(vehicleId);
  await forgetVehicleReferences(vehicleId);
  emitStoreChange('fleet');
  return true;
}

// ── The vehicle pool ─────────────────────────────────────────────────

/**
 * Writes the company's twenty-eight vehicles — on first run only, exactly as
 * {@link seedSoldierRoster} writes the roster.
 *
 * An empty table is the one signal that this is a first run, so a pool that
 * already holds *any* record is left completely alone. That is what makes the
 * seed safe to call on every mount: an edit stays, and a vehicle the operator
 * deleted stays deleted rather than returning on the next reload.
 *
 * Returns how many records this call wrote — `0` on every run but the first.
 */
export async function seedVehicleRoster(): Promise<number> {
  if ((await db.vehicles.count()) > 0) return 0;
  await db.vehicles.bulkPut(SEED_VEHICLES.map((vehicle) => ({ ...vehicle })));
  return SEED_VEHICLES.length;
}

/** Where the applied vehicle-pool revision is filed in the `meta` table. */
export const VEHICLE_ROSTER_REVISION_KEY = 'vehicle-roster-revision';

/** What one pass of {@link reconcileVehicleRoster} did — or did not — change. */
export interface VehicleReconciliation {
  /** `true` when this device was already on the current pool revision. */
  alreadyApplied: boolean;
  /** How many pool vehicles had to be written in. */
  restored: number;
}

/**
 * Brings a device that was running before the pool shipped onto it — once, and
 * once only.
 *
 * {@link seedVehicleRoster} fires on an empty table alone, which is right for a
 * fresh installation and useless for a phone whose operator has already typed a
 * vehicle or two in by hand: that table is not empty, so the seed never runs and
 * the twenty-eight real vehicles never arrive.
 *
 * Backfill only — there is nothing to purge. The pool has never been seeded with
 * demo records, so whatever a device already holds is the unit's own and is left
 * exactly as they left it. A vehicle is matched on the digits of its מספר צ, so
 * one the operator typed in themselves is recognised and skipped rather than
 * duplicated under the seed id.
 */
export async function reconcileVehicleRoster(): Promise<VehicleReconciliation> {
  const applied = (await readMeta<number>(VEHICLE_ROSTER_REVISION_KEY)) ?? 0;
  if (applied >= VEHICLE_ROSTER_REVISION) return { alreadyApplied: true, restored: 0 };

  const held = new Set(
    (await db.vehicles.toArray()).map((vehicle) => plateKey(vehicle.plateNumber)),
  );
  const missing = SEED_VEHICLES.filter((vehicle) => !held.has(plateKey(vehicle.plateNumber)));
  if (missing.length > 0) {
    await db.vehicles.bulkPut(missing.map((vehicle) => ({ ...vehicle })));
  }

  await writeMeta(VEHICLE_ROSTER_REVISION_KEY, VEHICLE_ROSTER_REVISION);
  return { alreadyApplied: false, restored: missing.length };
}

/** Replaces the command-level block list for a vehicle. */
export async function setVehicleBlockList(
  actor: User,
  vehicleId: string,
  soldierIds: readonly string[],
): Promise<Vehicle | null> {
  return updateVehicle(actor, vehicleId, { disallowedSoldierIds: [...soldierIds] });
}

// ── Soldiers ─────────────────────────────────────────────────────────

/**
 * Writes the company's official roster — on first run only, exactly as
 * `seedTaskTypes()` writes the shipped task catalogue.
 *
 * An empty table is the one signal that this is a first run, so a roster that
 * already holds *any* record is left completely alone. That is what makes the
 * seed safe to call on every mount: an edit stays, and a soldier the operator
 * deleted stays deleted rather than returning on the next reload.
 *
 * Returns how many records this call wrote — `0` on every run but the first.
 */
export async function seedSoldierRoster(): Promise<number> {
  if ((await db.soldiers.count()) > 0) return 0;
  await db.soldiers.bulkPut(SEED_SOLDIERS.map((soldier) => ({ ...soldier })));
  return SEED_SOLDIERS.length;
}

/** Where the applied roster revision is filed in the `meta` table. */
export const ROSTER_REVISION_KEY = 'roster-revision';

/** What one pass of {@link reconcileSoldierRoster} did — or did not — change. */
export interface RosterReconciliation {
  /** `true` when this device was already on the current roster revision. */
  alreadyApplied: boolean;
  /** Ids of the legacy demo records that were removed. */
  purged: string[];
  /** Demo records left in place because a convoy still crews them. */
  retained: string[];
  /** How many official soldiers had to be written back. */
  restored: number;
  /** Ids of the records whose misspelled name was corrected. */
  renamed: string[];
}

/**
 * Brings a database that was seeded by an older build onto the current roster —
 * once, and once only.
 *
 * `seedSoldierRoster()` fires on an empty table alone, which is right for a
 * fresh installation and useless for a phone that has been running since before
 * the roster existed: that table holds demo records, so the seed never runs and
 * the twenty real soldiers never arrive. This is the migration that fixes it.
 *
 * Two passes, in order:
 *
 * 1. **Purge.** Every record {@link isLegacyDemoSoldier} recognises is deleted,
 *    and every command block list that named it is swept in the same breath —
 *    the same clean removal `deleteSoldier()` performs. A demo record a convoy
 *    still crews is *not* deleted: removing it would leave a team pointing at a
 *    soldier who no longer exists, so it is reported in `retained` and left for
 *    the operator to unassign.
 * 2. **Backfill.** Every roster soldier the table is missing is written in,
 *    matched on the מ.א. A record the operator has since edited is matched and
 *    skipped, so their platoon, status and notes survive untouched.
 * 3. **Spelling.** Every correction in {@link ROSTER_NAME_CORRECTIONS} is
 *    applied to the record filed under that מ.א — and only while it still reads
 *    exactly as the bad revision wrote it, so a name the operator typed
 *    themselves is left alone. Nothing else on the record is touched.
 *
 * The revision is then filed in `meta`. Every later call returns immediately,
 * which is what keeps a soldier the operator deletes tomorrow from reappearing
 * on the next reload.
 */
export async function reconcileSoldierRoster(): Promise<RosterReconciliation> {
  const applied = (await readMeta<number>(ROSTER_REVISION_KEY)) ?? 0;
  if (applied >= ROSTER_REVISION) {
    return { alreadyApplied: true, purged: [], retained: [], restored: 0, renamed: [] };
  }

  const purged: string[] = [];
  const retained: string[] = [];
  for (const soldier of await db.soldiers.toArray()) {
    if (!isLegacyDemoSoldier(soldier)) continue;
    if ((await countSoldierAssignments(soldier.id)) > 0) {
      retained.push(soldier.id);
      continue;
    }
    await db.soldiers.delete(soldier.id);
    await forgetSoldierReferences(soldier.id);
    purged.push(soldier.id);
  }

  // The מ.א is what identifies a soldier, not the row's id — a record the unit
  // typed in by hand before the roster shipped is the same person, and is left
  // exactly as they typed it rather than duplicated under the seed id.
  const held = new Set((await db.soldiers.toArray()).map((soldier) => soldier.personalNumber));
  const missing = SEED_SOLDIERS.filter((soldier) => !held.has(soldier.personalNumber));
  if (missing.length > 0) {
    await db.soldiers.bulkPut(missing.map((soldier) => ({ ...soldier })));
  }

  const renamed: string[] = [];
  for (const correction of ROSTER_NAME_CORRECTIONS) {
    const stored = await getSoldierByPersonalNumber(correction.personalNumber);
    if (!stored || stored.fullName !== correction.from) continue;
    await db.soldiers.put({ ...stored, fullName: correction.to, updatedAt: nowIso() });
    renamed.push(stored.id);
  }

  await writeMeta(ROSTER_REVISION_KEY, ROSTER_REVISION);
  return { alreadyApplied: false, purged, retained, restored: missing.length, renamed };
}

/** The roster, by name — a soldier has no internal number to sort on. */
export async function listSoldiers(): Promise<Soldier[]> {
  const soldiers = await db.soldiers.toArray();
  return soldiers.sort((a, b) => a.fullName.localeCompare(b.fullName, 'he'));
}

export async function getSoldier(soldierId: string): Promise<Soldier | null> {
  return (await db.soldiers.get(soldierId)) ?? null;
}

export async function getSoldierByPersonalNumber(personalNumber: string): Promise<Soldier | null> {
  return (await db.soldiers.where('personalNumber').equals(personalNumber).first()) ?? null;
}

export async function createSoldier(actor: User, draft: SoldierDraft): Promise<Soldier> {
  assertPermission(actor, 'MANAGE_SOLDIERS');
  if (!String(draft.personalNumber ?? '').trim()) {
    throw new Error('Soldier personalNumber is required');
  }
  const stamp = nowIso();
  const soldier: Soldier = {
    ...draft,
    // One of the six, always — a job the roster no longer keeps is filed as נהג
    // on the way in rather than left for the screens to puzzle over.
    role: normalizeSoldierRole(draft.role),
    id: newId('sld'),
    createdAt: stamp,
    updatedAt: stamp,
    ...stampCreated(actor),
  };
  await db.soldiers.add(soldier);
  emitStoreChange('fleet');
  return soldier;
}

export async function updateSoldier(
  actor: User,
  soldierId: string,
  patch: Partial<SoldierDraft>,
): Promise<Soldier | null> {
  assertPermission(actor, 'MANAGE_SOLDIERS');
  const soldier = await getSoldier(soldierId);
  if (!soldier) return null;
  const merged = { ...soldier, ...patch };
  const updated: Soldier = {
    ...merged,
    role: normalizeSoldierRole(merged.role),
    updatedAt: nowIso(),
    // The one-tap status sheet writes through here too, so a "בבית עד יום
    // שלישי" set from a phone in the field is signed by whoever set it.
    ...stampUpdated(actor),
  };
  await db.soldiers.put(updated);
  // Every status tap and every form save passes through here, so this one call
  // is what puts "מי ביצע את השינוי" on a soldier's card. An empty diff writes
  // nothing.
  await recordFleetChange(
    actor,
    'soldier',
    updated.id,
    soldierChangeLabel(updated),
    diffSoldier(soldier, updated),
  );
  emitStoreChange('fleet');
  return updated;
}

/** Where the applied soldier-roles revision is filed in the `meta` table. */
export const SOLDIER_ROLE_REVISION_KEY = 'soldier-role-revision';

/** What one pass of {@link reconcileSoldierRoles} did — or did not — change. */
export interface SoldierRoleReconciliation {
  /** `true` when this device was already on the current roles revision. */
  alreadyApplied: boolean;
  /** Ids of the records whose retired job was refiled. */
  refiled: string[];
}

/**
 * Refiles every soldier the roster no longer has a job for — once, and once
 * only.
 *
 * The company keeps six jobs (`SOLDIER_ROLES`). A phone that has been running
 * since before that list was cut still holds מכונאי, מטעין, חובש and מוקדן, and
 * `normalizeSoldierRole()` keeps those records legible on the way to the screen
 * — but reading around them for ever is not the same as fixing them, and a
 * filter counts what is *stored*. This is the pass that writes the new job in:
 * the מ"פ stays the מ"פ and every other retired job becomes נהג, exactly as the
 * normaliser reads them.
 *
 * It carries its own revision key rather than riding on `ROSTER_REVISION`, so
 * bringing a device onto the new list can never re-run the roster purge or
 * resurrect a soldier the operator has since deleted. Nothing but the job is
 * touched.
 */
export async function reconcileSoldierRoles(): Promise<SoldierRoleReconciliation> {
  const applied = (await readMeta<number>(SOLDIER_ROLE_REVISION_KEY)) ?? 0;
  if (applied >= SOLDIER_ROLE_REVISION) return { alreadyApplied: true, refiled: [] };

  const stale = (await db.soldiers.toArray()).filter((soldier) =>
    isLegacySoldierRole(soldier.role),
  );
  if (stale.length > 0) {
    const stamp = nowIso();
    await db.soldiers.bulkPut(
      stale.map((soldier) => ({
        ...soldier,
        role: normalizeSoldierRole(soldier.role),
        updatedAt: stamp,
      })),
    );
  }

  await writeMeta(SOLDIER_ROLE_REVISION_KEY, SOLDIER_ROLE_REVISION);
  return { alreadyApplied: false, refiled: stale.map((soldier) => soldier.id) };
}

/**
 * How many crew slots a soldier holds. The roster asks this before it offers
 * the delete, exactly as the vehicle screen does.
 */
export async function countSoldierUsage(soldierId: string): Promise<number> {
  return countSoldierAssignments(soldierId);
}

/** Drops a departed soldier from every vehicle's command block list. */
async function forgetSoldierReferences(soldierId: string): Promise<void> {
  const vehicles = await db.vehicles.toArray();
  const touched = vehicles.filter((vehicle) =>
    (vehicle.disallowedSoldierIds ?? []).includes(soldierId),
  );
  if (touched.length === 0) return;
  const stamp = nowIso();
  await db.vehicles.bulkPut(
    touched.map((vehicle) => ({
      ...vehicle,
      disallowedSoldierIds: (vehicle.disallowedSoldierIds ?? []).filter((id) => id !== soldierId),
      updatedAt: stamp,
    })),
  );
}

/**
 * Removes a soldier for good, block lists and all. A soldier still crewed on a
 * convoy — as driver, escort or passenger — is refused with
 * {@link FleetRecordInUseError}; `false` means there was no such soldier.
 */
export async function deleteSoldier(actor: User, soldierId: string): Promise<boolean> {
  assertPermission(actor, 'MANAGE_SOLDIERS');
  if (!(await getSoldier(soldierId))) return false;
  const assignments = await countSoldierAssignments(soldierId);
  if (assignments > 0) {
    throw new FleetRecordInUseError(
      `לא ניתן למחוק חייל המשובץ ל־${assignments} משימות. יש להסיר אותו מהשיבוצים תחילה.`,
      assignments,
    );
  }
  await db.soldiers.delete(soldierId);
  await forgetSoldierReferences(soldierId);
  emitStoreChange('fleet');
  return true;
}

// ── Matching ─────────────────────────────────────────────────────────

/** Eligible drivers for a vehicle, best fit first. Empty when it is unknown. */
export async function listDriversForVehicle(
  vehicleId: string,
  options?: MatchOptions,
): Promise<DriverVehicleMatch[]> {
  const vehicle = await getVehicle(vehicleId);
  if (!vehicle) return [];
  return findEligibleDrivers(await db.soldiers.toArray(), vehicle, options);
}

/** The full verdict for one pairing, or `null` when either side is unknown. */
export async function matchDriverToVehicle(
  soldierId: string,
  vehicleId: string,
  options?: MatchOptions,
): Promise<DriverVehicleMatch | null> {
  const [soldier, vehicle] = await Promise.all([getSoldier(soldierId), getVehicle(vehicleId)]);
  if (!soldier || !vehicle) return null;
  return checkDriverVehicleMatch(soldier, vehicle, options);
}

// ── Maintenance ──────────────────────────────────────────────────────

/** Wipes both fleet tables — used by the "reset local data" action. */
export async function resetFleetData(): Promise<void> {
  await Promise.all([db.vehicles.clear(), db.soldiers.clear()]);
  emitStoreChange('fleet');
}
